/**
 * A card or drive read in the browser (design §6.1.1): read-only, through the
 * File System Access API where there is one (Chrome, Edge), else the folder
 * picker every browser has.
 *
 * @module
 */

export interface CardFile {
  file: File;
  /** Relative to the folder picked, e.g. "GPS/261009_144312_0071.gpx". */
  path: string;
}

export interface Card {
  /** The folder picked: often the drive itself, e.g. "E:" or "VAN7". */
  name: string;
  files: CardFile[];
}

interface DirectoryHandle {
  kind: "directory";
  name: string;
  values(): AsyncIterable<DirectoryHandle | FileHandle>;
}
interface FileHandle {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
}
type Picker = (options: {
  mode: "read";
  id?: string;
}) => Promise<DirectoryHandle>;

/** Whether the browser can open a folder directly (else use `fromInput`). */
export const canPickFolder = () =>
  typeof (window as unknown as { showDirectoryPicker?: Picker })
    .showDirectoryPicker === "function";

async function walk(dir: DirectoryHandle, prefix: string, out: CardFile[]) {
  for await (const entry of dir.values()) {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === "file") out.push({ file: await entry.getFile(), path });
    else await walk(entry, path, out);
  }
}

/** Opens the folder picker; null if the user cancels. */
export async function pickCard(): Promise<Card | null> {
  const picker = (window as unknown as { showDirectoryPicker: Picker })
    .showDirectoryPicker;
  let dir: DirectoryHandle;
  try {
    dir = await picker({ mode: "read", id: "avgv-card" });
  } catch {
    return null;
  }
  const files: CardFile[] = [];
  await walk(dir, "", files);
  return { name: dir.name, files };
}

/** From an `<input type="file" webkitdirectory>`. */
export function fromInput(list: FileList | null): Card | null {
  const all = Array.from(list ?? []);
  if (!all.length) return null;
  const name = all[0]?.webkitRelativePath.split("/")[0] ?? "";
  return {
    name,
    files: all.map((file) => ({
      file,
      path: file.webkitRelativePath.split("/").slice(1).join("/"),
    })),
  };
}

/** The top-level folder a file is in; "" for files at the top. */
export const topOf = (path: string): string =>
  path.includes("/") ? (path.split("/")[0] ?? "") : "";

/** The top-level folders, with what is in them. */
export function folders(card: Card): Map<string, CardFile[]> {
  const out = new Map<string, CardFile[]>();
  for (const f of card.files)
    out.set(topOf(f.path), [...(out.get(topOf(f.path)) ?? []), f]);
  return out;
}
